import { useState, useEffect, useMemo, useRef } from "react";
import ReactPaginate from 'react-paginate';

import PageHeader from "components/PageHeader";
import ReportForm from "components/ReportForm";
import userService from "services/user.service";
import TimeLineGroup from "./components/TimeLineGroup";
import styles from "./Tracking.module.scss";
import classnames from "classnames";
import TableGroup from "./components/TableGroup";
import DetailPopup from "./components/DetailPopup";
import {
  SYSTEM_TIMEZONE,
  getTimezoneOptions,
  shiftDateKey,
  transformReport,
} from "utils/trackingTransform";

const TrackingForOddo = ({ isLoggedIn, bypassToken }) => {
  const [employeesPhotos, setEmployeesPhotos] = useState(new Map());
  const [rawReport, setRawReport] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formError, setFormError] = useState("");
  const [showButton, setShowButton] = useState(false);
  const [isActiveTab, setActiveTab] = useState(1);
  const [timezone, setTimezone] = useState(SYSTEM_TIMEZONE);
  const [displayRange, setDisplayRange] = useState(null);

  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [popupDetails, setPopupDetails] = useState({});
  const [params, setParams] = useState({
    dateFrom: "",
    dateTo: "",
    employeesList: "",
  });
  const [paginationOddo, setPaginationOddo] = useState({limit: 25, offset:0})
  const [pageCount, setPageCount] = useState(0); // Total pages

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const trackingReport = useMemo(() => {
    if (!rawReport || !Array.isArray(rawReport) || !displayRange) return rawReport;
    return transformReport(rawReport, timezone, displayRange);
  }, [rawReport, timezone, displayRange]);

  const validate = (value) => {
    if (!value) {
      return "At least one team member must be selected.";
    }

    return "";
  };

  const handleOnSubmit = (e) => {

    e.preventDefault();

    setParams(params);

    setShowButton(false);

    const validationError = validate(params?.employeesList);
    setFormError(validationError);

    if (validationError !== "") {
      setRawReport("");
      setErrorMessage("");
      return;
    }

    // Buffer the API range by ±1 day so events near midnight in the user's
    // selected timezone still show up after re-bucketing. Note: pagination
    // total_records will reflect the buffered range.
    const apiDateFrom = shiftDateKey(params.dateFrom, -1);
    const apiDateTo = shiftDateKey(params.dateTo, 1);

    setDisplayRange({ from: params.dateFrom, to: params.dateTo });

    userService
      .getReportOddo(
        apiDateFrom,
        apiDateTo,
        params.employeesList,
        originUrl,
        bypassToken,
        paginationOddo,
      )
      .then(
        (response) => {
          setShowButton(true);
          setRawReport(response?.data?.data);
          setErrorMessage("");
          setPageCount(Math.ceil(response?.data?.total_records / paginationOddo.limit));
        },
        (error) => {
          const _content =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();

          setRawReport("");
          setErrorMessage(_content);
        },
      );
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1; // react-paginate is 0-based
    setPaginationOddo({ ...paginationOddo, offset: (selectedPage - 1) * paginationOddo.limit });
  };

  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      handleOnSubmit({ preventDefault: () => {} });
    } else {
      didMountRef.current = true;
    }
  }, [paginationOddo.offset]);

  const handleClick = (e) => {
    const currentIdNumber = +e.target.id;
    setActiveTab(currentIdNumber);
  };

  const [originUrl, setOriginUrl] = useState()

  useEffect(() => {

    const handleMessage = (event) => {
      const { origin, token } = event.data;
      if (origin && token) {
        setOriginUrl(origin)
      }
    };
    window.addEventListener('message', handleMessage);
    if(originUrl) window.removeEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage); // Cleanup
      setOriginUrl()
    };
  }, []);

  return (
    <>
      <div className="container">
        <PageHeader title="Calls & TRACKING" />
        <ReportForm
          isLoggedIn={isLoggedIn}
          originUrl={originUrl}
          bypassToken={bypassToken}
          handleOnSubmit={handleOnSubmit}
          showButton={showButton}
          params={params}
          formError={formError}
          employeesPhotos={employeesPhotos}
          setEmployeesPhotos={setEmployeesPhotos}
          withTime={false}
          timezone={timezone}
          setTimezone={setTimezone}
          timezoneOptions={timezoneOptions}
        />
        {errorMessage && (
          <div className={styles.wrapper}>{errorMessage}</div>
        )}
        {trackingReport && (
          <div className={styles.wrapper}>
            <div className={styles.tab_menu}>
              <button
                className={classnames(
                  styles.tab_item,
                  isActiveTab === 1 ? styles.active : "",
                )}
                id={1}
                onClick={handleClick}
              >
                Graphs
              </button>
              <button
                className={classnames(
                  styles.tab_item,
                  isActiveTab === 2 ? classnames(styles.active, styles.second) : "",
                )}
                id={2}
                onClick={handleClick}
              >
                Tables
              </button>
            </div>
            {isActiveTab === 1 && (
              <div className={styles.tab_container}>
                <div className={styles.content}>
                  {trackingReport?.map((item, index) => {
                    return (
                      <TimeLineGroup
                        group={item}
                        photos={Object.fromEntries(employeesPhotos)}
                        timezone={timezone}
                        key={index}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {isActiveTab === 2 && (
              <div className={styles.tab_container}>
                <div className={styles.content}>
                  {trackingReport?.map((item, index) => {
                    return (
                      <TableGroup
                        group={item}
                        photos={Object.fromEntries(employeesPhotos)}
                        timezone={timezone}
                        key={index}
                        isActiveTab={isActiveTab}
                        setShowDetailPopup={setShowDetailPopup}
                        setPopupDetails={setPopupDetails}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            <ReactPaginate
              previousLabel={<span>&larr;</span>}
              nextLabel={<span>&rarr;</span>}
              pageCount={pageCount}
              onPageChange={handlePageClick}
              containerClassName={styles.pagination}
              pageClassName={styles.pageButton}
              pageLinkClassName={styles.pageLink}
              previousClassName={styles.arrowButtonLeft}
              nextClassName={styles.arrowButtonRight}
              activeClassName={styles.active}
              disabledClassName={styles.disabled}
              breakClassName={styles.pageButton}
              breakLinkClassName={styles.pageLink}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
            />
          </div>
        )}

        {trackingReport && showDetailPopup && (
          <DetailPopup
            setShowDetailPopup={setShowDetailPopup}
            popupDetails={popupDetails}
            timezone={timezone}
          />
        )}
      </div>
    </>
  );
};

export default TrackingForOddo;
